import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  IconButton, 
  Button,
  Collapse,
  Grid,
  Chip,
  Menu,
  MenuItem
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  CalendarToday as CalendarIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as PlayArrowIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import ListItemIcon from '@mui/material/ListItemIcon';
import BacklogIssue from './BacklogIssue';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Helper function to extract company_id from token (like payroll)
const getCompanyIdFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    //console.error('No authentication token found');
    return null;
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.company_id;
  } catch (error) {
    //console.error('Error parsing token:', error);
    return null;
  }
};

const SprintSection = ({
  sprint,
  onDragOver,
  onDrop,
  onToggleExpanded,
  onStartSprint,
  onEditSprint,
  navigate,
  projectId,
  handleDragStart,
  issueTypes,
  statuses,
  priorities,
  getInitials,
  onSprintDeleted,
  onIssueClick,
  onAssigneeChange,
  onStatusChange,
  onParentChange,
  onMenuAction,
  onMove,
  onDelete,
  projectKey,
  searchQuery,
  activeFilters,
  hasActiveFilters,
  teamMembers = [] 
}) => {
  const isActive = sprint.status === 'ACTIVE';
  const isFuture = sprint.status === 'FUTURE';
  const isCompleted = sprint.status === 'COMPLETED';
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  const handleIssueClick = async (issue) => {
    try {
      // Fetch individual issue details to get proper names
      const company_id = getCompanyIdFromToken();
      const token = localStorage.getItem('token');
      if (!company_id) {
        //console.error('No company ID found');
        // Fallback to original issue data
        if (typeof onIssueClick === 'function') {
          onIssueClick(issue);
        }
        return;
      }

      const response = await fetch(`${API_URL}/api/issues/${issue.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      const data = await response.json();
      
      
      if (data.success) {
        // Pass the detailed issue data to the parent
        if (typeof onIssueClick === 'function') {
          onIssueClick(data.data);
        }
      } else {
        // Fallback to the original issue data
        if (typeof onIssueClick === 'function') {
          onIssueClick(issue);
        }
      }
    } catch (error) {
      //console.error('Error fetching issue details:', error);
      // Fallback to the original issue data
      if (typeof onIssueClick === 'function') {
        onIssueClick(issue);
      }
    }
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditSprint = () => {
    setMenuAnchorEl(null);
    if (typeof onEditSprint === 'function') {
      onEditSprint(sprint);
    }
  };

  const handleDeleteSprint = async () => {
    setMenuAnchorEl(null);

    if (!window.confirm('Are you sure you want to delete this sprint?')) return;
    const token = localStorage.getItem('token');
    // Extract company_id from token (like payroll)
    const company_id = getCompanyIdFromToken();
    if (!company_id) {
      alert('Authentication error. Please log in again.');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/sprints/delete/${sprint.id}`,
        {
          method: 'DELETE',
       
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      const data = await response.json();

      if (data.success) {
        if (typeof onSprintDeleted === 'function') {
          onSprintDeleted(sprint.id);
        }
        alert('Sprint deleted successfully!');
      } else {
        alert(data.message || 'Failed to delete sprint');
      }
    } catch (error) {
      alert('Error deleting sprint: ' + error.message);
    }
  };

  const renderSprintContent = () => {
    if (sprint.issues.length === 0) {
      
      const hasSearchQuery = searchQuery && searchQuery.trim() !== '';
      const hasTypeFilters = activeFilters && Object.values(activeFilters).some(Boolean);
      const hasActiveFilters = hasSearchQuery || hasTypeFilters;
      
      return (
        <Box sx={{ 
          py: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          textAlign: 'center',
          px: 2,
          color: 'text.secondary'
        }}>
          {hasActiveFilters ? (
            <Typography variant="body2">
              No issues match the current search or filter criteria.
            </Typography>
          ) : (
            <>
              <Typography variant="body2">
                Planning to work on something? Create an issue and add it to this sprint.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Drag and drop the issues here
              </Typography>
            </>
          )}
        </Box>
      );
    }
    
    return (
      <Box sx={{ p: 2 }}>
        <Grid container sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary', px: 2, py: 1 }}>
  <Grid item xs={4} sm={3} md={4} sx={{ textAlign: 'center' }}>SUMMARY</Grid>
  <Grid item xs={2} sm={1} md={1} sx={{ textAlign: 'center' }}>ISSUE TYPE</Grid>
  <Grid item xs={2} sm={2} md={2} sx={{ textAlign: 'center' }}>KEY</Grid>
  <Grid item xs={2} sm={2} md={2} sx={{ textAlign: 'center' }}>ASSIGNEE</Grid>
  <Grid item xs={1} sm={2} md={2} sx={{ textAlign: 'center' }}>STATUS</Grid>
  <Grid item xs={1} sm={2} md={1} sx={{ textAlign: 'center' }}>PRIORITY</Grid>
</Grid>
        
        {sprint.issues.map((issue) => (
          <BacklogIssue 
            key={issue.id}
            issue={issue}
            onDragStart={handleDragStart}
            onNavigate={() => handleIssueClick(issue)}
            projectId={projectId}
            issueTypes={issueTypes}
            statuses={statuses}
            priorities={priorities}
            getInitials={getInitials}
            onAssigneeChange={onAssigneeChange}
            onStatusChange={onStatusChange}
            onParentChange={onParentChange}
            onMenuAction={onMenuAction}
            onMove={onMove}
            projectKey={projectKey}
            assignees={teamMembers} 
            onDelete={onDelete}
          />
        ))}
      </Box>
    );
  };
  
  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        overflow: 'overflow',
        mb: 2,
        borderRadius: '3px',
        boxShadow: '0 1px 1px rgba(9, 30, 66, 0.25)'
      }}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <Box 
        sx={{ 
          borderBottom: sprint.expanded ? '1px solid #DFE1E6' : 'none', 
          bgcolor: '#F4F5F7', 
          px: 2, 
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <IconButton 
            size="small" 
            sx={{ p: 0.5, mr: 1 }}
            onClick={onToggleExpanded}
          >
            <ExpandMoreIcon 
              fontSize="small" 
              sx={{ 
                transform: sprint.expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s'
              }} 
            />
          </IconButton>
          
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                {sprint.name}
              </Typography>
              
              {isActive && (
                <Chip
                  label="ACTIVE"
                  size="small"
                  sx={{ 
                    ml: 1,
                    height: 20,
                    bgcolor: '#36B37E', 
                    color: 'white',
                    fontSize: '0.7rem',
                    fontWeight: 500
                  }}
                />
              )}
              
              {isCompleted && (
                <Chip
                  label="COMPLETED"
                  size="small"
                  sx={{ 
                    ml: 1,
                    height: 20,
                    bgcolor: '#6B73FF', 
                    color: 'white',
                    fontSize: '0.7rem',
                    fontWeight: 500
                  }}
                />
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {sprint.startDate && sprint.endDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(sprint.startDate)} - {formatDate(sprint.endDate)}
                  </Typography>
                </Box>
              )}
              
              <Typography variant="caption" color="text.secondary">
                {sprint.issues.length} issue{sprint.issues.length !== 1 ? 's' : ''}
              </Typography>
              
              {sprint.goal && (
                <Typography variant="caption" color="text.secondary" sx={{ 
                  fontStyle: 'italic',
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  Goal: {sprint.goal}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isFuture && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={onStartSprint}
              sx={{
                textTransform: 'none',
                borderColor: '#0052CC',
                color: '#0052CC',
                '&:hover': {
                  borderColor: '#0747A6',
                  backgroundColor: 'rgba(0, 82, 204, 0.04)'
                }
              }}
            >
              Start Sprint
            </Button>
          )}
          
          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleEditSprint}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <span>Edit Sprint</span>
            </MenuItem>
            <MenuItem 
              onClick={handleDeleteSprint}
              disabled={isActive}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color={isActive ? "disabled" : "error"} />
              </ListItemIcon>
              <span style={{ 
                color: isActive ? '#999' : '#d32f2f', 
                fontWeight: 500 
              }}>
                Delete Sprint
              </span>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      
      <Collapse in={sprint.expanded}>
        <Box sx={{ bgcolor: 'white' }}>
          {renderSprintContent()}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default SprintSection;